import { getter } from '@progress/kendo-react-common'
import { KeyValue } from '@progress/kendo-react-form'
import { isValidYear } from './helpers'

export const RequiredValidator = (value: string | number | Date) => value ? '' : 'This field is required.'

export const EmailValidator = (value: string) =>
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        .test(value)
        ? ''
        : 'Please enter a valid email ex. myemail@example.com'

export const EmailValidatorNotRequired = (value: string) =>
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        .test(value) || value == '' || value == undefined
        ? ''
        : 'Please enter a valid email ex. myemail@example.com'

export const PhoneValidator = (value: string) =>
    /^\(?\d{3}\)?[- ]?\d{3}[- ]?\d{4}$/.test(value)
        ? ''
        : 'Please enter a valid phone number ex. 502-326-8413'

export const PhoneValidatorNotRequired = (value: string) =>
    /^\(?\d{3}\)?[- ]?\d{3}[- ]?\d{4}$/.test(value) || value == '' || value == undefined
        ? ''
        : 'Please enter a valid phone number ex. 502-326-8413'

export const DashOnlyPhoneValidator = (value: string) =>
    /^\d{3}-\d{3}-\d{4}$/.test(value)
        ? ''
        : 'Please enter a valid phone number ex. 502-326-8413'

export const DashOnlyPhoneValidatorNotRequired = (value: string) =>
    /^\d{3}-\d{3}-\d{4}$/.test(value) || value == '' || value == undefined
        ? ''
        : 'Please enter a valid phone number ex. 502-326-8413'

export const YoutubeLinkValidator = (youTubeLink: string) => {

    let link = youTubeLink ?? ''
    if (link == '')
        return ''

    if (!link.includes('https://'))
        return 'Please include https:// in your link'

    if (!(link.includes('youtu.be') || link.includes('youtube.com')))
        return 'Enter a valid YouTube link.'

    let url = link.replace(/(>|<)/gi, '').split(/(vi\/|v=|\/v\/|youtu\.be\/|\/shorts\/|\/embed\/)/)

    if (url[2] !== undefined) {
        let videoId = url[2].split(/[^0-9a-z_\-]/i)[0]
        if (videoId)
            return '' // Valid URL
    }

    return 'Enter a valid YouTube link.'
}

// form validation summary for start/end date
export const startendValidator = (values?: KeyValue<string> | undefined) => {
    const startGetter = getter('startDateInput')
    const endGetter = getter('endDateInput')
    if (startGetter(values) > endGetter(values)) {
        return {
            VALIDATION_SUMMARY: 'invalid date range',
            'startDateInput':
                'Start Date cannot be after End Date',
            'endDateInput':
                'End Date cannot be before Start Date'
        }
    }
    return undefined
}

export const futureDateValidator = (values?: KeyValue<string> | undefined) => {
    const monthGetter = getter('ccMonth')
    const yearGetter = getter('ccYear')
    let year = null
    let month = null
    const monthSelect = monthGetter(values)
    const yearSelect = yearGetter(values)

    if (yearSelect) {
        year = parseInt(yearSelect.yearText.toString())
    }
    if (monthSelect) {
        month = monthSelect.monthId
    }
    const now = new Date()
    let dateValue = null
    if (year && month) {
        dateValue = new Date(year, month - 1)
    }
    else {
        return
    }
    if (dateValue < now) {
        return {
            VALIDATION_SUMMARY: 'invalid date range',
            'ccMonth':
                'Invalid Expiration Date Combo',
            'ccYear':
                'Invalid Expiration Date Combo'
        }
    }
    return undefined
}

export const ColorPickerHexValidator = (value: string) =>
    value.includes('#') && value.length == 7
        ? ''
        : 'Please use hex format for color input.'

export const ZipCodeValidator = (value: string) =>
    /^[0-9.-]*$/.test(value) || value == '' || value == undefined
        ? ''
        : 'Please enter a valid zip code ex. 40220 or 40220-1234'

export const ZipCodeWithLengthValidator = (value: string) =>
    /^\d{5}$/.test(value)
        ? ''
        : 'Please enter a valid zip code ex. 40220'

export const ZipCodeWithLengthValidatorNotRequired = (value: string) =>
    /^\d{5}$/.test(value) || value == '' || value == undefined
        ? ''
        : 'Please enter a valid zip code ex. 40220'

export const EmailOrPhoneRequired = (email: string, phone: string) => email || phone ? undefined : 'A valid email or phone # is required.'

export const matchValidator = (value1: string, value2: string, message: string) => value1?.trim() === value2?.trim() ? undefined : message

export const lengthValidator = (value: string, length: number) => {

    if (!value || value.length != length)
        return `must be ${length} characters.`
    return ''
}

export const minimumLengthValidator = (value: string, length: number) => {

    if (!value || value.length < length)
        return `must be at least ${length} characters.`
    return ''
}

export const NumberValidator = (value: string) =>
    /^-?[0-9.,]+$/.test(value)
        ? ''
        : 'Please enter a valid number'

export const PercentValidator = (value: string) =>
    /^[0-9.,]+$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= 100
        ? ''
        : 'Please enter a percent between 1 and 100'

export const NumberValidatorNotRequired = (value: string) =>
    /^-?[0-9.,]+$/.test(value) || value == '' || value == undefined
        ? ''
        : 'Please enter a valid number'

export const PercentValidatorNotRequired = (value: string) =>
    /^[0-9.,]+$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= 100 || value == '' || value == undefined
        ? ''
        : 'Please enter a percent between 1 and 100'

export const PercentValidatorAllowUnderOne = (value: string) =>
    /^[0-9.,]+$/.test(value) && parseFloat(value) >= 0.01 && parseFloat(value) <= 100
        ? ''
        : 'Please enter a percent between 0 and 100'

export const ValidatorTwoDecimalNotRequired = (value: string) => /^\d+(?:\.\d{1,2})?$/.test(value) || value == '' || value == undefined
    ? ''
    : 'Please enter a value with up to two decimal digits.'

export const NumberValidatorNoDecimals = (value: string) =>
    /^-?[0-9]+$/.test(value) || value == '' || value == undefined
        ? ''
        : 'Please enter a valid whole number'

export const TrueValidator = (value: boolean) => value ? '' : 'This field is required.'

export const MaxDateRangeValidator = (values?: KeyValue<string>) => {
    const startGetter = getter('startDateInput')
    const endGetter = getter('endDateInput')
    const maxRangeGetter = getter('maxRangeInput')
    const startDate = new Date(startGetter(values))
    const endDate = new Date(endGetter(values))
    const maxRange = maxRangeGetter(values)

    if (startDate > endDate) {
        return {
            VALIDATION_SUMMARY: 'invalid date range',
            'startDateInput': 'Start Date cannot be after End Date',
            'endDateInput': 'End Date cannot be before Start Date'
        }
    }

    const dayDifference = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24)
    if (maxRange !== undefined && dayDifference > maxRange) {
        return {
            VALIDATION_SUMMARY: 'invalid max date range',
            'startDateInput': `Date range cannot be longer than ${maxRange} days`,
            'endDateInput': ''
        }
    }
    return undefined
}

//Datespan Invalid Start and End date validation
export const invalidDateValidator = (startDate?: Date | null, endDate?: Date | null) => {
    return {
        VALIDATION_SUMMARY: 'invalid date',
        'startDateInput':
            startDate && isValidYear(startDate) ? '' : 'Invalid Date',
        'endDateInput':
            endDate && isValidYear(endDate) ? '' : 'Invalid Date'
    }
}